import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaClock,
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaCheck
} from 'react-icons/fa';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import PageMeta from '../../components/PageMeta';
import api from '../../utils/api';
import {
  CONTACT_EMAIL,
  CONTACT_PHONE,
  CONTACT_PHONE_TEL,
  CONTACT_MAILTO,
  CONTACT_ADDRESS_LINE1,
  CONTACT_ADDRESS_LINE2,
  SOCIAL_LINKS,
} from '../../config/siteContact';
import './Contact.css';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await api.post('/contact', formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setTimeout(() => setSubmitStatus(null), 5000);
    } catch (err) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      <PageMeta
        title={t('contact.title')}
        description={t('contact.subtitle')}
        path="/contact"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero-container">
          <h1 className="contact-hero-title">{t('contact.title')}</h1>
          <p className="contact-hero-subtitle">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <div className="contact-container">
          <div className="contact-wrapper">
            {/* Contact Information */}
            <div className="contact-info-side">
              <h2 className="contact-info-title">{t('contact.getInTouch')}</h2>
              <p className="contact-info-text">
                {t('contact.getInTouchDesc')}
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-icon" aria-hidden>
                    <FaEnvelope />
                  </div>
                  <div className="contact-detail-content">
                    <h3>{t('contact.emailLabel')}</h3>
                    <p>
                      <a href={CONTACT_MAILTO} className="contact-detail-link">{CONTACT_EMAIL}</a>
                    </p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon" aria-hidden>
                    <FaPhone />
                  </div>
                  <div className="contact-detail-content">
                    <h3>{t('contact.phoneLabel')}</h3>
                    <p>
                      <a href={CONTACT_PHONE_TEL} className="contact-detail-link">{CONTACT_PHONE}</a>
                    </p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon" aria-hidden>
                    <FaMapMarkerAlt />
                  </div>
                  <div className="contact-detail-content">
                    <h3>{t('contact.addressLabel')}</h3>
                    <p>{CONTACT_ADDRESS_LINE1}</p>
                    <p>{CONTACT_ADDRESS_LINE2}</p>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon" aria-hidden>
                    <FaClock />
                  </div>
                  <div className="contact-detail-content">
                    <h3>{t('contact.hoursLabel')}</h3>
                    <p>{t('contact.hours1')}</p>
                    <p>{t('contact.hours2')}</p>
                  </div>
                </div>
              </div>

              <div className="contact-social-media-section">
                <h3 className="contact-social-title">{t('contact.followUs')}</h3>
                <div className="contact-social-links">
                  <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                    <FaFacebook className="contact-social-link-icon" aria-hidden />
                    Facebook
                  </a>
                  <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                    <FaInstagram className="contact-social-link-icon" aria-hidden />
                    Instagram
                  </a>
                  <a href={SOCIAL_LINKS.twitter} target="_blank" rel="noopener noreferrer" className="contact-social-link">
                    <FaTwitter className="contact-social-link-icon" aria-hidden />
                    Twitter
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-side">
              <h2 className="contact-form-title">{t('contact.sendMessage')}</h2>
              
              {submitStatus === 'error' && (
                <div className="contact-form-error-message" role="alert">
                  <span>{t('contact.errorMessage')}</span>
                </div>
              )}

              {submitStatus === 'success' && (
                <div className="contact-form-success-message" role="status">
                  <FaCheck className="contact-form-success-icon" aria-hidden />
                  <span>{t('contact.successMessage')}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="contact-form-group">
                  <label htmlFor="name" className="contact-form-label">{t('contact.fullName')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="contact-form-input"
                    required
                    placeholder={t('contact.namePlaceholder')}
                  />
                </div>

                <div className="contact-form-row">
                  <div className="contact-form-group">
                    <label htmlFor="email" className="contact-form-label">{t('contact.email')}</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="contact-form-input"
                      required
                      placeholder={t('contact.emailPlaceholder')}
                    />
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="phone" className="contact-form-label">{t('contact.phone')}</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="contact-form-input"
                      placeholder={t('contact.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div className="contact-form-group">
                  <label htmlFor="subject" className="contact-form-label">{t('contact.subject')}</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="contact-form-input"
                    required
                  >
                    <option value="">{t('contact.selectSubject')}</option>
                    <option value="general">{t('contact.generalInquiry')}</option>
                    <option value="farmer">{t('contact.farmerRegistration')}</option>
                    <option value="consumer">{t('contact.consumerSupport')}</option>
                    <option value="technical">{t('contact.technicalSupport')}</option>
                    <option value="partnership">{t('contact.partnership')}</option>
                    <option value="other">{t('contact.other')}</option>
                  </select>
                </div>

                <div className="contact-form-group">
                  <label htmlFor="message" className="contact-form-label">{t('contact.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="contact-form-input contact-form-textarea"
                    required
                    rows="6"
                    placeholder={t('contact.messagePlaceholder')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="contact-submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.sending') : t('contact.sendBtn')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;

